/**
 * CTO links section — the link generator of admin-requirements §2.9 / FR-A34
 * (epic 024 task 04).
 *
 * A CTO Email campaign is a named container; this section holds its **link
 * rows**: traffic source × promo type × rate, plus the merge tag,
 * custom params and the per-link kill switch. (The per-link Turnstile/
 * EmailOversight checkboxes retired with the Chelsie matrix — epic 108 task
 * 07: the click's email domain decides the capture, so there is nothing left
 * to pick per link.) The platform generates the URL —
 * computed server-side from `CTO_LINK_BASE_URL` + the row (ADR-007 #1) and
 * shown read-only with a Copy action — so nothing is typed: no slug, no date,
 * no ClickMagick.
 *
 * Two modes (epic 037-06, operator ruling 2026-09-09 — reverses 024 README
 * decision #5):
 * - **edit** (default) — rows persist through their own endpoints
 *   (`POST/PATCH/DELETE /admin/campaigns/:id/links`), independently of the
 *   campaign's Save button. The Active toggle is the one exception: on a saved
 *   row it persists immediately — it is the kill switch (Chelsie 01:26), a
 *   lifecycle action like the campaign's own start/pause buttons, not a form
 *   field.
 * - **create** — the campaign is not saved yet, so the rows are client-local
 *   drafts: no api calls at all (no row fetch, no per-row save, no stats).
 *   The drafts ride the aggregate campaign create payload's `links` array and
 *   are attached server-side in the same transaction as the parent; the
 *   "Generated link" cell shows a placeholder until then (a final URL embeds
 *   the link row's DB PK — ADR-007 #1 — so it cannot pre-exist).
 *
 * HF-139 (QA-37): the CTO Links directory links to `/campaigns/:id#cto-link-<id>`.
 * In edit mode, once the rows have loaded, the section scrolls that row to the
 * centre once per mount and rings it for ~2.5 s. An id that is not on the page
 * does nothing — no toast, no error. It reads `window.location.hash` (not a
 * router hook): the section is also rendered without a Router in its tests.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, type CampaignLinkRow, type CampaignLinkStatsResponse } from '../lib/api';
import type { CreateCampaignLinkInput, PromoTypeResponse } from '@leads-platform/shared';
import {
  CTO_DEFAULT_MERGE_TAG,
  createCampaignLinkSchema,
  ctoMergeTagPresets,
} from '@leads-platform/shared';
import { Button } from './ui/button';
import { FormSection, Field, FieldBlock } from './ui/form-section';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { Toggle } from './ui/toggle';
import { CopyButton } from './ui/copy-button';
import { Typeahead } from './typeahead';
import { useToast } from './ui/toast';
import { DateRangeField } from './date-range-field';

// --- Stats date helpers (task 024-09) ---

/** yyyy-MM-dd for today in the browser's local date. */
function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** [from, to] for the current calendar month (browser local date). */
function thisMonthRange(): [string, string] {
  const today = todayYmd();
  return [`${today.slice(0, 7)}-01`, today];
}

/** [from, to] for the previous calendar month (browser local date). */
function lastMonthRange(): [string, string] {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const last = new Date(d.getFullYear(), d.getMonth(), 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return [fmt(first), fmt(last)];
}

type StatsPreset = 'this_month' | 'last_month' | 'custom';

/**
 * The site reference a link row needs — the numeric `site.id` PK (the write
 * key, 004-13 convention) plus the label and the `site_id` business key
 * (display + type-ahead search). The campaign form already holds exactly this
 * shape for the restricted-site picker.
 */
export type CtoLinkSite = { id: string; siteId: string; label: string };

/**
 * A→Z by label, case-insensitively, tie-broken by numeric site id ascending
 * (HF-081's comparator, `screens/campaigns.tsx:522-523`). Driver: Chelsie's
 * QA-07, 9/26 note — "Change traffic source drop down so it's alphabetical."
 */
function bySiteLabel(a: CtoLinkSite, b: CtoLinkSite): number {
  const cmp = a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  return cmp !== 0 ? cmp : Number(a.id) - Number(b.id);
}

interface LinkDraft {
  /** Stable React key — the server id once saved, a row counter before that. */
  key: string;
  /** null until the link has been persisted. */
  id: string | null;
  siteId: string;
  promoTypeId: string;
  rate: string;
  mergeTag: string;
  customParams: { key: string; value: string }[];
  status: 'active' | 'inactive';
  /** The server-computed URL; empty while the row is unsaved. */
  url: string;
  /** Editable-field snapshot the Save button diffs against ('' when dirty). */
  saved: string;
}

export type { LinkDraft };

/** The DOM id of a saved link row — the directory's deep-link hash and the row share it (HF-139). */
export function ctoLinkAnchorId(linkId: string): string {
  return `cto-link-${linkId}`;
}

/** How long the jumped-to row stays ringed (HF-139). */
const HIGHLIGHT_MS = 2500;

let draftCounter = 0;
const nextDraftKey = () => `draft-${++draftCounter}`;

function snapshot(fields: Omit<LinkDraft, 'key' | 'id' | 'url' | 'saved'>): string {
  return JSON.stringify(fields);
}

function fieldsOf(d: LinkDraft): Omit<LinkDraft, 'key' | 'id' | 'url' | 'saved'> {
  const { siteId, promoTypeId, rate, mergeTag, customParams, status } = d;
  return { siteId, promoTypeId, rate, mergeTag, customParams, status };
}

function draftFromRow(row: CampaignLinkRow): LinkDraft {
  const draft: LinkDraft = {
    key: `link-${row.id}`,
    id: row.id,
    siteId: row.siteId,
    promoTypeId: row.promoTypeId,
    rate: row.price == null ? '' : String(row.price),
    mergeTag: row.mergeTag,
    customParams: (row.customParams ?? []).map((p) => ({ key: p.key, value: p.value })),
    status: row.status,
    url: row.url,
    saved: '',
  };
  return { ...draft, saved: snapshot(fieldsOf(draft)) };
}

/**
 * The blank row — optionally seeded with the previous row's traffic source and
 * rate (Sep-15 call §AG-2: not repicking those for every link). Everything
 * else stays at its static default, and the editable-field snapshot is taken
 * after seeding, so a seeded draft is clean relative to itself — the same
 * dirty semantics as a blank row (id is null, so its Save is never disabled).
 */
function emptyDraft(seed?: Pick<LinkDraft, 'siteId' | 'rate'>): LinkDraft {
  const draft: LinkDraft = {
    key: nextDraftKey(),
    id: null,
    siteId: seed?.siteId ?? '',
    promoTypeId: '',
    rate: seed?.rate ?? '',
    mergeTag: CTO_DEFAULT_MERGE_TAG,
    customParams: [],
    status: 'active',
    url: '',
    saved: '',
  };
  return { ...draft, saved: snapshot(fieldsOf(draft)) };
}

/**
 * The wire payload for one draft. The Turnstile/EmailOversight checkboxes are
 * retired from the editor (the Chelsie matrix, epic 108 task 07 — the email
 * domain decides the capture, globally), so the payload omits `validations`
 * and the api's schema default fills both flags ON — the stored row stays
 * valid and the wire contract is unchanged.
 */
export function payloadFor(d: LinkDraft, rate: number): CreateCampaignLinkInput {
  return {
    siteId: d.siteId,
    promoTypeId: d.promoTypeId,
    price: rate,
    // Retired checkboxes (Chelsie matrix, epic 108 task 07): the empty block
    // lets the api schema's defaults fill both flags ON, keeping every stored
    // row valid without the editor carrying the toggles.
    validations: {},
    mergeTag: d.mergeTag.trim() || CTO_DEFAULT_MERGE_TAG,
    customParams: d.customParams
      .filter((p) => p.key.trim() !== '')
      .map((p) => ({ key: p.key.trim(), value: p.value })),
    status: d.status,
  };
}

/**
 * Validate one draft before it may be saved — the per-row Save button and the
 * create-mode campaign-Save gate (epic 037-06) share this, so the row errors
 * and the gate cannot diverge. The friendly field checks come first; the
 * shared `createCampaignLinkSchema.safeParse` over the wire body is the
 * backstop for everything only the schema knows (param key pattern, merge-tag
 * length, …).
 */
export function draftFieldErrors(d: LinkDraft): string[] {
  const rowErrors: string[] = [];
  if (!d.siteId) rowErrors.push('Pick a traffic source.');
  if (!d.promoTypeId) rowErrors.push('Pick a promo type.');
  const rate = Number(d.rate);
  if (!d.rate.trim() || !Number.isFinite(rate) || rate < 0) {
    rowErrors.push('Enter the rate.');
  }
  if (rowErrors.length > 0) return rowErrors;

  const parsed = createCampaignLinkSchema.safeParse(payloadFor(d, rate));
  if (!parsed.success) {
    rowErrors.push(
      ...parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`)
    );
  }
  return rowErrors;
}

function RowErrors({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <ul className="mt-3 space-y-1 text-sm text-destructive" role="alert">
      {messages.map((message) => (
        <li key={message}>{message}</li>
      ))}
    </ul>
  );
}

/** One editable link row. */
function LinkRow({
  campaignId,
  draft,
  sites,
  promoTypes,
  createMode = false,
  highlighted = false,
  formErrors = [],
  onSaved,
  onRemoved,
  onChange,
  onDirty,
}: {
  campaignId: string;
  draft: LinkDraft;
  sites: CtoLinkSite[];
  promoTypes: PromoTypeResponse[];
  /** Create mode: client-local draft — no per-row Save, no kill switch. */
  createMode?: boolean;
  /** Ringed for a moment after the directory deep-link jumped here (HF-139). */
  highlighted?: boolean;
  /** Row errors pushed down by the campaign-Save gate (create mode). */
  formErrors?: string[];
  onSaved: (row: CampaignLinkRow) => void;
  onRemoved: (key: string) => void;
  onChange: (key: string, changes: Partial<LinkDraft>) => void;
  onDirty: (key: string) => void;
}) {
  const { success, error: toastError } = useToast();
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // Merge-tag override (043-02): collapsed by default, but open on mount when
  // the row carries a non-default tag — a stored custom tag must never hide
  // behind a closed toggle. The initializer runs at mount only, so a row the
  // operator edits afterwards keeps whatever state they chose.
  const [advancedOpen, setAdvancedOpen] = useState(draft.mergeTag !== CTO_DEFAULT_MERGE_TAG);

  const site = sites.find((s) => s.id === draft.siteId) ?? null;
  const promo = promoTypes.find((p) => p.id === draft.promoTypeId) ?? null;
  const dirty = draft.saved === '' || snapshot(fieldsOf(draft)) !== draft.saved;

  const edit = (changes: Partial<LinkDraft>) => {
    onChange(draft.key, changes);
    onDirty(draft.key);
  };

  const save = async () => {
    const rowErrors = draftFieldErrors(draft);
    setErrors(rowErrors);
    if (rowErrors.length > 0) return;

    setSaving(true);
    try {
      const body = payloadFor(draft, Number(draft.rate));
      const savedRow =
        draft.id === null
          ? await api.createCampaignLink(campaignId, body)
          : await api.updateCampaignLink(campaignId, draft.id, body);
      onSaved(savedRow);
      success(draft.id === null ? 'Link added' : 'Link updated');
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to save the link');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (draft.id === null) {
      // An unsaved draft is client-local state — discard it, nothing to delete
      // on the server.
      onRemoved(draft.key);
      return;
    }
    try {
      await api.deleteCampaignLink(campaignId, draft.id);
      onRemoved(draft.key);
      success('Link deleted');
    } catch (err) {
      const apiError = err as ApiError;
      // A 409 reads "…set it inactive instead" — the operator's way out.
      toastError(apiError.message || 'Failed to delete the link');
    }
  };

  const toggleStatus = async (next: boolean) => {
    const status = next ? ('active' as const) : ('inactive' as const);
    onChange(draft.key, { status });
    if (draft.id === null) return; // an unsaved row persists with its first save
    try {
      const updated = await api.updateCampaignLink(campaignId, draft.id, { status });
      // The PATCH carried only the status, so merge only that back — the row may
      // hold unsaved edits that a wholesale replace would silently drop.
      onChange(draft.key, { status: updated.status });
      success(next ? 'Link active' : 'Link inactive');
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to update the link');
      onChange(draft.key, { status: next ? 'inactive' : 'active' }); // revert the switch
    }
  };

  return (
    <div
      id={draft.id ? ctoLinkAnchorId(draft.id) : undefined}
      className={`rounded-lg border p-4 transition${highlighted ? ' ring-2 ring-accent' : ''}`}
      data-testid="cto-link-row"
      data-highlighted={highlighted ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="text-xs font-semibold text-ink">Generated link</span>
          {draft.url ? (
            <div className="mt-1 flex items-center gap-2 rounded-lg bg-paper px-3 py-2 hairline">
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">
                {draft.url}
              </span>
              <CopyButton value={draft.url} title="Copy the CTO link" />
            </div>
          ) : createMode ? (
            // No final URL before the campaign save — the generated URL embeds
            // the link row's DB PK (ADR-007 #1), which does not exist yet.
            <p className="mt-1 text-xs italic text-faint">
              URL appears after save — utm_content=&lt;pending&gt;.
            </p>
          ) : (
            <p className="mt-1 text-xs italic text-faint">
              The URL generates when the link is saved.
            </p>
          )}
        </div>
        {!createMode && (
          <Toggle
            checked={draft.status === 'active'}
            onChange={(checked) => void toggleStatus(checked)}
            label={draft.status === 'active' ? 'Active' : 'Inactive'}
            hint={
              draft.id === null
                ? 'Persists when the link is saved.'
                : 'Applies immediately — an inactive link sends clicks to tradingtips.com.'
            }
          />
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Traffic source" required>
          <Typeahead<CtoLinkSite>
            options={sites}
            value={draft.siteId || null}
            onChange={(v) => edit({ siteId: v ?? '' })}
            getId={(s) => s.id}
            getLabel={(s) => s.label}
            getSubtitle={(s) => `Site ID ${s.siteId}`}
            placeholder="Select a traffic source…"
          />
          <span className="text-xs leading-snug text-faint">
            Type to search by name or site ID{site ? ` — selected ${site.siteId}` : ''}.
          </span>
        </Field>

        <Field label="Promo type" required>
          <Select value={draft.promoTypeId} onChange={(e) => edit({ promoTypeId: e.target.value })}>
            <option value="">Select a promo type…</option>
            {promoTypes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.slug})
              </option>
            ))}
          </Select>
          <span className="text-xs leading-snug text-faint">
            The slug is the <span className="font-mono">utm_campaign</span> value.
          </span>
        </Field>

        <Field label="Rate (USD)" required>
          <Input
            mono
            placeholder="1.50"
            value={draft.rate}
            onChange={(e) => edit({ rate: e.target.value })}
          />
        </Field>

        {/* Merge tag (043-02): every link defaults to CTO_DEFAULT_MERGE_TAG;
            the ESP preset dropdown left with Q101. What remains is the tag as
            read-only text plus an Advanced disclosure for the per-link
            override (emptyDraft pre-fills the constant, payloadFor falls back
            to it on an emptied field). FieldBlock, not Field: Field renders a
            <label>, and a label's control is its first labelable descendant —
            with the Advanced button present that would be the button, and
            clicks anywhere on the block would toggle it. */}
        <FieldBlock label="Merge tag" hint="The sending ESP substitutes the tag per recipient.">
          {/* What the link actually carries — payloadFor saves the constant
              when the Advanced field is emptied, so the display falls back the
              same way. */}
          <span className="font-mono text-sm text-ink" data-testid="merge-tag-value">
            {draft.mergeTag.trim() || CTO_DEFAULT_MERGE_TAG}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-fit"
            data-testid="merge-tag-advanced"
            aria-expanded={advancedOpen}
            onClick={() => setAdvancedOpen((open) => !open)}
          >
            Advanced
          </Button>
          {advancedOpen && (
            <>
              <Input
                mono
                id="cto-merge-tag-input"
                aria-label="Custom merge tag"
                value={draft.mergeTag}
                placeholder="{{contact.email}}"
                onChange={(e) => edit({ mergeTag: e.target.value })}
              />
              {/* The presets stay as a hint (README decision 5): the vendor
                  dialects of the same tag, not a choice to make. */}
              <span className="text-xs leading-snug text-faint">
                Vendor presets: {ctoMergeTagPresets.map((p) => p.label).join(' · ')}
              </span>
            </>
          )}
        </FieldBlock>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-ink">Custom parameters</span>
          {draft.customParams.map((param, index) => (
            <div key={index} className="flex items-center gap-2">
              {/* Widths on wrapper divs, not on Input: Input's base has w-full,
                  and cn (clsx, no tailwind-merge) can't override it — a w-32
                  class on Input loses to w-full in the CSS cascade and the key
                  field swallowed the whole row. */}
              <div className="w-32 shrink-0">
                <Input
                  mono
                  placeholder="key"
                  aria-label={`Custom parameter ${index + 1} key`}
                  value={param.key}
                  onChange={(e) =>
                    edit({
                      customParams: draft.customParams.map((p, i) =>
                        i === index ? { ...p, key: e.target.value } : p
                      ),
                    })
                  }
                />
              </div>
              <div className="min-w-0 flex-1">
                <Input
                  mono
                  placeholder="value"
                  aria-label={`Custom parameter ${index + 1} value`}
                  value={param.value}
                  onChange={(e) =>
                    edit({
                      customParams: draft.customParams.map((p, i) =>
                        i === index ? { ...p, value: e.target.value } : p
                      ),
                    })
                  }
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  edit({ customParams: draft.customParams.filter((_, i) => i !== index) })
                }
              >
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                edit({ customParams: [...draft.customParams, { key: '', value: '' }] })
              }
            >
              Add parameter
            </Button>
          </div>
          <span className="text-xs leading-snug text-faint">
            Appended to the link verbatim, in this order (e.g.{' '}
            <span className="font-mono">utm_traffic=ABC</span>).
          </span>
        </div>
      </div>

      <RowErrors messages={[...formErrors, ...errors]} />

      <div className="mt-4 flex items-center justify-end gap-2">
        {site && promo && (
          <span className="mr-auto text-xs text-faint">
            {site.label} · {promo.name}
          </span>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={() => void remove()}>
          Delete
        </Button>
        {!createMode && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={saving || (draft.id !== null && !dirty)}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : draft.id === null ? 'Save link' : dirty ? 'Save link' : 'Saved'}
          </Button>
        )}
      </div>
    </div>
  );
}

export function CtoLinksSection({
  campaignId,
  sites,
  mode = 'edit',
  onDraftsChange,
  draftErrors = {},
}: {
  /** The parent campaign id — edit mode only (create mode has no id yet). */
  campaignId?: string;
  sites: CtoLinkSite[];
  /** 'edit' (default): rows persist through their own endpoints. 'create': client-local drafts riding the campaign create payload. */
  mode?: 'edit' | 'create';
  /** Create mode: the form reads the drafts when it builds the create payload. */
  onDraftsChange?: (rows: LinkDraft[]) => void;
  /** Create mode: row errors from the campaign-Save gate, keyed by draft key. */
  draftErrors?: Record<string, string[]>;
}) {
  const createMode = mode === 'create';
  const { error: toastError } = useToast();
  // Sorted once here (not mutating the `sites` prop) so every Typeahead the
  // rows render lists traffic sources A→Z (HF-093).
  const sortedSites = useMemo(() => [...sites].sort(bySiteLabel), [sites]);
  const [rows, setRows] = useState<LinkDraft[]>([]);
  const [promoTypes, setPromoTypes] = useState<PromoTypeResponse[]>([]);
  const [loaded, setLoaded] = useState(false);
  // HF-139: the deep-link jump fires once per mount; the timer outlives the
  // jump effect's re-runs (any `rows` change re-runs it), so a ref holds it.
  const jumpDone = useRef(false);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // --- Stats state (task 024-09) ---
  const [statsPreset, setStatsPreset] = useState<StatsPreset>('this_month');
  const [statsFrom, setStatsFrom] = useState(() => thisMonthRange()[0]);
  const [statsTo, setStatsTo] = useState(() => thisMonthRange()[1]);
  const [stats, setStats] = useState<CampaignLinkStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (createMode) {
      // An unsaved campaign has no rows to fetch and nothing to measure —
      // only the promo-type picklist is needed.
      let cancelled = false;
      api
        .getPromoTypes()
        .then((promos) => {
          if (cancelled) return;
          setPromoTypes(promos.filter((p) => p.status === 'active'));
          setLoaded(true);
        })
        .catch((err: ApiError) => {
          if (cancelled) return;
          setLoaded(true);
          toastError(err.message || 'Failed to load the promo types');
        });
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    setLoaded(false);
    Promise.all([api.getCampaignLinks(campaignId ?? ''), api.getPromoTypes()])
      .then(([links, promos]) => {
        if (cancelled) return;
        setRows(links.map(draftFromRow));
        setPromoTypes(promos.filter((p) => p.status === 'active'));
        setLoaded(true);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setLoaded(true);
        toastError(err.message || 'Failed to load the CTO links');
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, createMode]);

  // HF-139 (QA-37): `/campaigns/:id#cto-link-<id>` — scroll that saved row to
  // the centre and ring it for HIGHLIGHT_MS. Runs once, after the first load
  // commits the rows. The timer is deliberately not cleared by this effect's
  // cleanup (a `rows` change or a StrictMode remount re-runs it and `jumpDone`
  // would block re-arming); the mount-only effect below clears it.
  useEffect(() => {
    if (createMode || !loaded || jumpDone.current) return;
    jumpDone.current = true;
    const match = /^#cto-link-(\d+)$/.exec(window.location.hash);
    if (!match || !rows.some((r) => r.id === match[1])) return;
    document
      .getElementById(ctoLinkAnchorId(match[1]))
      ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    setHighlightedId(match[1]);
    highlightTimer.current = setTimeout(() => setHighlightedId(null), HIGHLIGHT_MS);
  }, [loaded, rows, createMode]);

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    },
    []
  );

  // Create mode: bridge the draft rows to the form — it builds the aggregate
  // payload's `links` array from them at save time (epic 037-06).
  useEffect(() => {
    if (createMode) onDraftsChange?.(rows);
  }, [createMode, onDraftsChange, rows]);

  // Fetch stats whenever the effective date range changes.
  const fetchStats = useCallback(
    (from: string, to: string) => {
      if (!from || !to) return;
      setStatsLoading(true);
      api
        .getCampaignLinkStats(campaignId ?? '', from, to)
        .then((data) => {
          setStats(data);
        })
        .catch(() => {
          // Stats are non-critical; a failed fetch leaves the previous data.
        })
        .finally(() => {
          setStatsLoading(false);
        });
    },
    [campaignId]
  );

  useEffect(() => {
    if (createMode) return; // nothing saved to measure — no stats fetch
    fetchStats(statsFrom, statsTo);
  }, [createMode, fetchStats, statsFrom, statsTo]);

  const applyPreset = (preset: StatsPreset) => {
    setStatsPreset(preset);
    if (preset === 'this_month') {
      const [from, to] = thisMonthRange();
      setStatsFrom(from);
      setStatsTo(to);
    } else if (preset === 'last_month') {
      const [from, to] = lastMonthRange();
      setStatsFrom(from);
      setStatsTo(to);
    }
    // 'custom' — user edits the DateRangeField directly
  };

  const patchRow = (key: string, changes: Partial<LinkDraft>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const markDirty = (key: string) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, saved: '' } : row)));

  const replaceRow = (key: string, saved: CampaignLinkRow) =>
    setRows((current) => current.map((row) => (row.key === key ? draftFromRow(saved) : row)));

  // Helper: look up display names for stats rows.
  const siteLabel = (siteId: string) => sites.find((s) => s.id === siteId)?.label ?? siteId;
  const promoName = (promoTypeId: string) =>
    promoTypes.find((p) => p.id === promoTypeId)?.name ?? promoTypeId;

  return (
    <FormSection
      title="CTO links"
      desc="Each row is a link a traffic source can run: source × promo type × rate. The platform generates the URL from the picks — nothing is typed — and stats and billing are per link per source (§2.9)."
    >
      <div className="space-y-4 sm:col-span-2">
        {rows.map((row) => (
          <LinkRow
            key={row.key}
            // Create mode reaches no api path (no Save, no toggle; Delete is a
            // local discard) — the empty id is never used on a call.
            campaignId={campaignId ?? ''}
            draft={row}
            sites={sortedSites}
            promoTypes={promoTypes}
            createMode={createMode}
            highlighted={row.id !== null && row.id === highlightedId}
            formErrors={draftErrors[row.key] ?? []}
            onSaved={(saved) => replaceRow(row.key, saved)}
            onRemoved={(key) => setRows((current) => current.filter((r) => r.key !== key))}
            onChange={patchRow}
            onDirty={markDirty}
          />
        ))}

        {loaded && rows.length === 0 && <p className="text-xs italic text-faint">No links yet.</p>}

        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setRows((current) => {
                // Carry the last row's source + rate into the next draft — a
                // saved row or an unsaved draft, both count (Sep-15 §AG-2).
                const last = current[current.length - 1];
                return [
                  ...current,
                  emptyDraft(last ? { siteId: last.siteId, rate: last.rate } : undefined),
                ];
              })
            }
          >
            Add link
          </Button>
        </div>

        {/* Stats panel — date-range control + per-link table (task 024-09).
            Hidden in create mode: nothing is saved to measure or export. */}
        {!createMode && (
          <div className="border-t border-line pt-4 mt-2" data-testid="cto-stats-panel">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-ink">Stats</span>
              <div className="flex gap-1">
                {(
                  [
                    ['this_month', 'This month'],
                    ['last_month', 'Last month'],
                    ['custom', 'Custom'],
                  ] as const
                ).map(([preset, label]) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
                      statsPreset === preset
                        ? 'bg-accent text-white'
                        : 'bg-paper text-faint hover:text-ink hairline'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {statsPreset === 'custom' && (
                <div className="w-64">
                  <DateRangeField
                    from={statsFrom}
                    to={statsTo}
                    onChange={(from, to) => {
                      setStatsFrom(from);
                      setStatsTo(to);
                    }}
                  />
                </div>
              )}
              {statsLoading && <span className="text-xs text-faint">Loading…</span>}
              {/* Export CSV — FR-A35 reconciliation download (task 024-13) */}
              <button
                type="button"
                data-testid="campaign-export-csv"
                onClick={() =>
                  window.open(api.campaignExportUrl(campaignId ?? '', statsFrom, statsTo))
                }
                className="rounded-md px-2 py-0.5 text-xs font-medium bg-paper text-faint hover:text-ink hairline transition"
              >
                Export CSV
              </button>
            </div>

            {stats && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="cto-stats-table">
                  <thead>
                    <tr className="text-left text-faint border-b border-line">
                      <th className="pb-1 pr-3 font-medium">Link</th>
                      <th className="pb-1 pr-3 font-medium text-right">Leads</th>
                      <th className="pb-1 pr-3 font-medium text-right">Unique</th>
                      <th className="pb-1 pr-3 font-medium text-right">Success</th>
                      <th className="pb-1 pr-3 font-medium text-right">Duplicate</th>
                      <th className="pb-1 pr-3 font-medium text-right">Not checked</th>
                      <th className="pb-1 font-medium text-right">Rejected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      // A draft joins no stat row: its URL embeds the row's
                      // DB PK (ADR-007 #1), which does not exist before the
                      // save — zeros are the only honest numbers for it.
                      const stat = stats.links.find((s) => s.linkId === row.id) ?? {
                        leads: 0,
                        uniqueEmails: 0,
                        success: 0,
                        duplicate: 0,
                        unchecked: 0,
                        rejected: 0,
                      };
                      return (
                        <tr key={row.key} className="border-b border-line/50">
                          <td className="py-1 pr-3 text-ink">
                            {row.siteId && row.promoTypeId
                              ? `${siteLabel(row.siteId)} · ${promoName(row.promoTypeId)}`
                              : '—'}
                            {row.id === null && (
                              <span className="italic text-faint"> · not saved yet</span>
                            )}
                          </td>
                          <td className="py-1 pr-3 text-right tabular-nums">{stat.leads}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">{stat.uniqueEmails}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">{stat.success}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">{stat.duplicate}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">{stat.unchecked}</td>
                          <td className="py-1 text-right tabular-nums">{stat.rejected}</td>
                        </tr>
                      );
                    })}
                    <tr className="font-semibold text-ink">
                      <td className="pt-2 pr-3">Campaign total</td>
                      <td className="pt-2 pr-3 text-right tabular-nums">{stats.campaign.leads}</td>
                      <td className="pt-2 pr-3 text-right tabular-nums">
                        {stats.campaign.uniqueEmails}
                      </td>
                      <td className="pt-2 pr-3 text-right" />
                      <td className="pt-2 pr-3 text-right" />
                      <td className="pt-2 pr-3 text-right" />
                      <td className="pt-2 text-right tabular-nums">{stats.campaign.rejected}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </FormSection>
  );
}
